import {
  ZadLabelBatch,
  ZadLabelBatchItem,
  ZadLabelBatchOptions,
  ZadLabelBatchSourceType,
  ZadLabelBatchStatus,
  ZadLabelBatchWriteItem,
  ZadLabelBatchWritePayload,
  ZadLabelPrintFormat,
  ZadLabelType,
  ZadProduct,
  ZadPurchase,
  ZadPurchaseItem,
} from '../../../models/zad'
import {ZadBarcodeLabelFormat, validateZadLabelQuantity} from './barcodeHelpers'
import {preserveZadBarcode} from './productHelpers'

export const ZAD_LABEL_BATCH_QTY_PRESETS = [1, 2, 5, 10, 20, 50, 100] as const
export const ZAD_LABEL_SHEET = {
  widthMm: 50,
  heightMm: 30,
  a4Columns: 3,
  gapMm: 4,
  a4MarginMm: 8,
} as const

export type {ZadLabelType, ZadLabelBatchStatus, ZadLabelBatchSourceType, ZadLabelPrintFormat}

export type ZadLabelPrintCopy = {
  productId: string
  productCode: string
  barcode: string
  productName: string
  salePriceSnapshot: number
  labelType: ZadLabelType
  copyIndex: number
}

export const EMPTY_ZAD_LABEL_BATCH_OPTIONS: ZadLabelBatchOptions = {
  showPrice: true,
  showReference: true,
  showProductName: true,
  showBarcode: true,
}

export function defaultZadLabelBatchOptions(): ZadLabelBatchOptions {
  return {...EMPTY_ZAD_LABEL_BATCH_OPTIONS}
}

export function labelBatchProductId(item: Pick<ZadLabelBatchItem, 'product'> | {productId?: string}): string {
  if ('productId' in item && item.productId) return String(item.productId)
  const product = (item as ZadLabelBatchItem).product
  if (typeof product === 'string') return product
  return product?._id || ''
}

export function toZadLabelCssFormat(format?: ZadLabelPrintFormat | ZadBarcodeLabelFormat | string): ZadBarcodeLabelFormat {
  return String(format || '').toUpperCase() === 'A4' || format === 'a4' ? 'a4' : 'thermal'
}

export function toZadLabelPrintFormat(format?: ZadLabelPrintFormat | ZadBarcodeLabelFormat | string): ZadLabelPrintFormat {
  return String(format || '').toUpperCase() === 'A4' || format === 'a4' ? 'A4' : 'THERMAL'
}

export function emptyUnsavedZadLabelBatch(overrides?: Partial<ZadLabelBatch>): ZadLabelBatch {
  return {
    _id: '',
    batchNumber: '',
    status: 'DRAFT',
    sourceType: 'MANUAL',
    printFormat: 'THERMAL',
    options: defaultZadLabelBatchOptions(),
    items: [],
    productCount: 0,
    labelCount: 0,
    printCount: 0,
    ...overrides,
  }
}

export function createZadLabelBatchItemFromProduct(
  product: Pick<ZadProduct, '_id' | 'code' | 'barcode' | 'name' | 'salePrice'>,
  quantity = 1,
  labelType: ZadLabelType = 'PRODUCT_LABEL'
): ZadLabelBatchItem {
  const parsed = validateZadLabelQuantity(quantity)
  return {
    product: product._id,
    productCode: preserveZadBarcode(product.code),
    productName: product.name || '',
    barcode: preserveZadBarcode(product.barcode) || null,
    salePriceSnapshot: Number(product.salePrice) || 0,
    quantity: parsed.ok ? parsed.value : 1,
    labelType,
  }
}

export function upsertZadLabelBatchItem(
  items: ZadLabelBatchItem[],
  incoming: ZadLabelBatchItem
): ZadLabelBatchItem[] {
  const incomingId = labelBatchProductId(incoming)
  const existing = items.find((item) => labelBatchProductId(item) === incomingId)
  if (!existing) return [...items, incoming]
  const parsed = validateZadLabelQuantity(existing.quantity + incoming.quantity)
  return items.map((item) =>
    labelBatchProductId(item) === incomingId
      ? {...item, quantity: parsed.ok ? parsed.value : existing.quantity}
      : item
  )
}

export function setZadLabelBatchQuantity(
  items: ZadLabelBatchItem[],
  productId: string,
  quantity: string | number
): {ok: true; items: ZadLabelBatchItem[]} | {ok: false; errorId: string} {
  const parsed = validateZadLabelQuantity(quantity)
  if (!parsed.ok) return parsed
  return {
    ok: true,
    items: items.map((item) =>
      labelBatchProductId(item) === productId ? {...item, quantity: parsed.value} : item
    ),
  }
}

export function applyZadLabelBatchBulkQuantity(
  items: ZadLabelBatchItem[],
  quantity: string | number,
  productIds?: string[]
): {ok: true; items: ZadLabelBatchItem[]} | {ok: false; errorId: string} {
  const parsed = validateZadLabelQuantity(quantity)
  if (!parsed.ok) return parsed
  const selected = new Set(productIds && productIds.length ? productIds : items.map(labelBatchProductId))
  return {
    ok: true,
    items: items.map((item) =>
      selected.has(labelBatchProductId(item)) ? {...item, quantity: parsed.value} : item
    ),
  }
}

export function removeZadLabelBatchItem(items: ZadLabelBatchItem[], productId: string): ZadLabelBatchItem[] {
  return items.filter((item) => labelBatchProductId(item) !== productId)
}

export function countZadLabelBatchTotals(items: ZadLabelBatchItem[]): {products: number; labels: number} {
  return {
    products: items.length,
    labels: items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
  }
}

export function expandZadLabelBatchCopies(items: ZadLabelBatchItem[]): ZadLabelPrintCopy[] {
  const copies: ZadLabelPrintCopy[] = []
  items.forEach((item) => {
    const parsed = validateZadLabelQuantity(item.quantity)
    const count = parsed.ok ? parsed.value : 0
    const barcode = preserveZadBarcode(item.barcode)
    for (let copyIndex = 0; copyIndex < count; copyIndex += 1) {
      copies.push({
        productId: labelBatchProductId(item),
        productCode: preserveZadBarcode(item.productCode),
        barcode,
        productName: item.productName,
        salePriceSnapshot: item.salePriceSnapshot,
        labelType: item.labelType,
        copyIndex,
      })
    }
  })
  return copies
}

export function getZadPurchaseLineProductId(item: ZadPurchaseItem): string {
  if (typeof item.product === 'string') return item.product
  return item.product?._id || ''
}

export function createZadLabelBatchItemsFromPurchase(
  purchase: Pick<ZadPurchase, 'items'>,
  products: Array<Pick<ZadProduct, '_id' | 'code' | 'barcode' | 'name' | 'salePrice'>>
): ZadLabelBatchItem[] {
  const byId = new Map(products.map((product) => [product._id, product]))
  return (purchase.items || []).reduce<ZadLabelBatchItem[]>((rows, line) => {
    const productId = getZadPurchaseLineProductId(line)
    const populated = typeof line.product === 'object' && line.product ? line.product : byId.get(productId)
    if (!populated?._id) return rows
    const qty = Number.isInteger(line.quantity) && line.quantity > 0 ? line.quantity : 1
    return upsertZadLabelBatchItem(rows, createZadLabelBatchItemFromProduct(populated, qty))
  }, [])
}

export function toZadLabelBatchWriteItems(items: ZadLabelBatchItem[]): ZadLabelBatchWriteItem[] {
  return items.map((item) => ({
    product: labelBatchProductId(item),
    quantity: item.quantity,
    labelType: item.labelType === 'PRICE_TAG' ? 'PRICE_TAG' : 'PRODUCT_LABEL',
  }))
}

const FORBIDDEN_WRITE_FIELDS = [
  'batchNumber',
  'status',
  'productCount',
  'labelCount',
  'printCount',
  'salePriceSnapshot',
  'purchasePrice',
  'unitCostSnapshot',
  'costTotal',
  'grossMargin',
  'effectiveNetUnitCost',
  'listUnitPrice',
]

export function sanitizeZadLabelBatchWritePayload(
  payload: ZadLabelBatchWritePayload
): ZadLabelBatchWritePayload {
  const body: ZadLabelBatchWritePayload = {}
  if (payload.sourceType) body.sourceType = payload.sourceType
  if (payload.sourceId) body.sourceId = payload.sourceId
  if (payload.printFormat) body.printFormat = toZadLabelPrintFormat(payload.printFormat)
  if (payload.options) {
    body.options = {
      showPrice: Boolean(payload.options.showPrice),
      showReference: Boolean(payload.options.showReference),
      showProductName: Boolean(payload.options.showProductName),
      showBarcode: Boolean(payload.options.showBarcode),
    }
  }
  if (payload.items) {
    body.items = payload.items.map((item) => ({
      product: String(item.product),
      quantity: item.quantity,
      labelType: item.labelType === 'PRICE_TAG' ? 'PRICE_TAG' : 'PRODUCT_LABEL',
    }))
  }
  if (payload.notes !== undefined) body.notes = String(payload.notes)
  FORBIDDEN_WRITE_FIELDS.forEach((field) => {
    delete (body as Record<string, unknown>)[field]
  })
  return body
}

export function buildManualLabelBatchCreatePayload(
  items: ZadLabelBatchItem[],
  options?: Partial<ZadLabelBatchOptions>,
  printFormat?: ZadLabelPrintFormat
): ZadLabelBatchWritePayload {
  return sanitizeZadLabelBatchWritePayload({
    sourceType: 'MANUAL',
    printFormat: printFormat || 'THERMAL',
    options: {...defaultZadLabelBatchOptions(), ...options},
    items: toZadLabelBatchWriteItems(items),
  })
}

export function buildSourceLabelBatchCreatePayload(
  sourceType: 'PURCHASE' | 'PURCHASE_ORDER',
  sourceId: string
): ZadLabelBatchWritePayload {
  return sanitizeZadLabelBatchWritePayload({sourceType, sourceId})
}

export function isZadLabelBatchEditable(batch?: ZadLabelBatch | null): boolean {
  return Boolean(batch && batch.status === 'DRAFT')
}

export function canPrintZadLabelBatch(batch?: ZadLabelBatch | null): boolean {
  return Boolean(batch && batch.status === 'READY')
}

export function canRefreshZadLabelBatch(batch?: ZadLabelBatch | null): boolean {
  return Boolean(batch && (batch.status === 'DRAFT' || batch.status === 'READY'))
}

export function canCancelZadLabelBatch(batch?: ZadLabelBatch | null): boolean {
  return Boolean(batch && (batch.status === 'DRAFT' || batch.status === 'READY'))
}

export function canCreateZadPurchaseOrderLabels(document?: {
  documentType?: string
  status?: string
} | null): boolean {
  return Boolean(
    document &&
      document.documentType === 'PURCHASE_ORDER' &&
      (document.status === 'CONFIRMED' || document.status === 'SENT')
  )
}

export function refreshedFromId(value: ZadLabelBatch['refreshedFrom']): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  return value._id || value.batchNumber || ''
}

export function mapPhase1LocalDraftToWritePayload(raw: unknown): ZadLabelBatchWritePayload | null {
  if (!raw || typeof raw !== 'object') return null
  const draft = raw as Record<string, any>
  if (draft.status && draft.status !== 'DRAFT') return null
  const rawSource = String(draft.sourceType || draft.source || 'MANUAL')
  const sourceType: ZadLabelBatchSourceType =
    rawSource === 'PURCHASE' || rawSource === 'PURCHASE_ORDER' ? rawSource : 'MANUAL'
  const items = Array.isArray(draft.items)
    ? draft.items
        .map((item: any) => {
          const product = item.productId || labelBatchProductId(item)
          const quantity = Number(item.quantity)
          if (!product || !Number.isInteger(quantity) || quantity < 1) return null
          const labelType: ZadLabelType =
            item.labelType === 'PRICE_TAG' || item.labelType === 'PRICE' ? 'PRICE_TAG' : 'PRODUCT_LABEL'
          return {product: String(product), quantity, labelType}
        })
        .filter(Boolean)
    : []
  if (sourceType === 'MANUAL' && !items.length) return null
  const options = draft.options || {}
  return sanitizeZadLabelBatchWritePayload({
    sourceType,
    sourceId: draft.sourceId || undefined,
    printFormat: toZadLabelPrintFormat(options.format || draft.printFormat),
    options: {
      showPrice: options.showPrice !== false,
      showReference: options.showReference !== false,
      showProductName: options.showProductName !== false && options.showName !== false,
      showBarcode: options.showBarcode !== false,
    },
    items: sourceType === 'MANUAL' ? (items.filter(Boolean) as ZadLabelBatchWriteItem[]) : items.length ? (items.filter(Boolean) as ZadLabelBatchWriteItem[]) : undefined,
  })
}

export const ZAD_LABEL_BATCH_API = {
  list: 'GET /api/zad/label-batches',
  create: 'POST /api/zad/label-batches',
  read: 'GET /api/zad/label-batches/:id',
  update: 'POST /api/zad/label-batches/:id/update',
  ready: 'POST /api/zad/label-batches/:id/ready',
  cancel: 'POST /api/zad/label-batches/:id/cancel',
  recordPrint: 'POST /api/zad/label-batches/:id/record-print',
  refreshPrices: 'POST /api/zad/label-batches/:id/refresh-prices',
} as const
